<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useLossReasonsStore } from 'dashboard/stores/lossReasons';
import Button from 'dashboard/components-next/button/Button.vue';

const emit = defineEmits(['confirm', 'cancel']);

const { t } = useI18n();
const lossReasonsStore = useLossReasonsStore();

const isOpen = ref(false);
const selectedReasonId = ref(null);
const pending = ref(null);

const reasonsList = computed(() => lossReasonsStore.getList);

const open = context => {
  pending.value = context;
  selectedReasonId.value = null;
  isOpen.value = true;
  lossReasonsStore.get();
};

const confirm = () => {
  emit('confirm', { ...pending.value, lossReasonId: selectedReasonId.value });
  isOpen.value = false;
};

const cancel = () => {
  emit('cancel');
  isOpen.value = false;
};

defineExpose({ open });
</script>

<template>
  <div
    v-if="isOpen"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
    @click.self="cancel"
  >
    <div
      class="bg-n-solid-1 rounded-2xl shadow-xl w-full max-w-sm mx-4 p-6 flex flex-col gap-5"
    >
      <div class="flex flex-col gap-1">
        <h3 class="text-base font-semibold text-n-slate-12">
          {{ t('DEALS.LOST_MODAL.TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-10">
          {{ t('DEALS.LOST_MODAL.DESCRIPTION') }}
        </p>
      </div>

      <div class="flex flex-col gap-2 max-h-64 overflow-y-auto">
        <button
          v-for="reason in reasonsList"
          :key="reason.id"
          type="button"
          class="flex items-center gap-3 px-3 py-2.5 rounded-lg text-left text-sm transition-colors"
          :class="
            selectedReasonId === reason.id
              ? 'bg-n-ruby-3 text-n-ruby-11 font-medium'
              : 'text-n-slate-11 hover:bg-n-alpha-2'
          "
          @click="
            selectedReasonId = selectedReasonId === reason.id ? null : reason.id
          "
        >
          <span
            class="w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center"
            :class="
              selectedReasonId === reason.id
                ? 'border-n-ruby-9 bg-n-ruby-9'
                : 'border-n-slate-7'
            "
          >
            <span
              v-if="selectedReasonId === reason.id"
              class="w-1.5 h-1.5 rounded-full bg-white"
            />
          </span>
          {{ reason.name }}
        </button>
        <p
          v-if="!lossReasonsStore.uiFlags.fetching && !reasonsList.length"
          class="text-xs text-n-slate-9 text-center py-2"
        >
          {{ t('DEALS.LOST_MODAL.NO_REASONS') }}
        </p>
      </div>

      <div class="flex gap-2 justify-end">
        <Button
          :label="t('DEALS.LOST_MODAL.CANCEL')"
          variant="ghost"
          color="slate"
          @click="cancel"
        />
        <Button
          :label="t('DEALS.LOST_MODAL.CONFIRM')"
          color="ruby"
          @click="confirm"
        />
      </div>
    </div>
  </div>
</template>
